/**
 * Lançar e editar um título — rotas `/app/financeiro/lancamentos/novo` e
 * `/app/financeiro/lancamentos/:numero/editar`.
 *
 * ## Por que saiu da gaveta
 *
 * Este formulário morava dentro de uma gaveta lateral, e a gaveta tinha sido
 * alargada de 480 para 560 px justamente porque os pares de campo não cabiam —
 * o comentário que justificava a largura estava lá, e era o sintoma.
 *
 * São doze campos em seis grupos. Mas o que decidiu não foi o tamanho: era o
 * DESCARTE. A gaveta fecha no Esc e no clique no fundo, sem confirmação e sem
 * desfazer, e o sistema não tem proteção de rascunho em lugar nenhum. Quem
 * preenchia doze campos e encostava o mouse no fundo escuro perdia tudo, sem
 * aviso.
 *
 * Gaveta é para ver e para decidir uma coisa. Formulário longo é tela, com
 * endereço próprio — e aí o Esc não faz nada, o Voltar do navegador funciona e
 * o F5 não é uma armadilha.
 *
 * A baixa e a exclusão continuam em DIÁLOGO: são uma decisão cada, com dois
 * campos, e o contexto da linha atrás importa.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { LinkBotao } from '../../componentes/Botao'
import { mostrarAviso } from '../../componentes/avisos'
import { ErroApi } from '../../plataforma/api'
import { carregarContas, type Lado, type LinhaConta } from '../../plataforma/financeiro'
import { LayoutApp } from '../app/LayoutApp'
import { FormularioDeLancamento } from './FormularioDeLancamento'

const CAMINHO_LISTA = '/app/financeiro'

/** `receber` é o padrão: é o lado com mais lançamento manual numa loja. */
function ladoDaBusca(bruto: string | null): Lado {
  return bruto === 'pagar' ? 'pagar' : 'receber'
}

export function TelaNovoLancamento() {
  const [busca] = useSearchParams()
  const lado = ladoDaBusca(busca.get('lado'))

  return <Moldura lado={lado} />
}

export function TelaEdicaoLancamento() {
  const { numero = '' } = useParams<{ numero: string }>()
  const [busca] = useSearchParams()
  const lado = ladoDaBusca(busca.get('lado'))

  const [linha, setLinha] = useState<LinhaConta | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erroDeCarga, setErroDeCarga] = useState('')

  /*
   * A API não tem `GET /financeiro/contas/{numero}` — o contrato expõe a
   * LISTA, com filtro. Então a tela busca a lista do lado certo e acha a linha
   * pelo número do documento, que é a identidade de negócio do título
   * (CONSTITUICAO.md §2 — nunca o uuid).
   *
   * Isto é honesto sobre o que existe hoje. No dia em que o endpoint de item
   * existir, troca-se esta função e mais nada.
   */
  useEffect(() => {
    const controle = new AbortController()
    setCarregando(true)

    carregarContas(lado, { busca: numero }, controle.signal)
      .then((dados) => {
        const achada = dados.linhas.find((item) => item.numero_documento === numero)
        if (!achada) {
          setErroDeCarga('Este título não está mais em aberto ou não existe.')
          return
        }
        setLinha(achada)
        setErroDeCarga('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErroDeCarga(
          causa instanceof ErroApi ? causa.message : 'Não foi possível carregar o título.',
        )
      })
      .finally(() => setCarregando(false))

    return () => controle.abort()
  }, [numero, lado])

  if (carregando) {
    return (
      <LayoutApp largura="formulario" titulo="Editar lançamento" subtitulo="Carregando…">
        <div className="h-[420px] w-full animate-pulse rounded-sistema bg-superficie" />
      </LayoutApp>
    )
  }

  if (erroDeCarga || !linha) {
    return (
      <LayoutApp
        largura="formulario"
        trilha={[{ rotulo: 'Financeiro', para: CAMINHO_LISTA }, { rotulo: numero }]}
        titulo="Editar lançamento"
        subtitulo={numero}
      >
        <div className="flex w-full flex-col gap-4">
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
            {erroDeCarga || 'Título não encontrado.'}
          </div>
          <div>
            <LinkBotao para={CAMINHO_LISTA} variante="secundario" icone="arrow_back">
              Voltar ao financeiro
            </LinkBotao>
          </div>
        </div>
      </LayoutApp>
    )
  }

  return <Moldura lado={lado} edicao={linha} />
}

function Moldura({ lado, edicao }: { lado: Lado; edicao?: LinhaConta }) {
  const navegar = useNavigate()
  const editando = Boolean(edicao)

  /*
   * O LADO viaja de volta no state da rota.
   *
   * As abas do financeiro ainda são estado interno, então `/app/financeiro`
   * sozinho abre na visão geral. Quem lançou uma conta a pagar espera voltar
   * para "A pagar" e ver o título que acabou de criar — não para um resumo do
   * mês onde ele precisa achar a aba de novo.
   */
  const voltar = () => navegar(CAMINHO_LISTA, { state: { aba: lado } })

  return (
    <LayoutApp
      largura="formulario"
      trilha={[
        { rotulo: 'Financeiro', para: CAMINHO_LISTA },
        { rotulo: lado === 'pagar' ? 'A pagar' : 'A receber', para: CAMINHO_LISTA },
        { rotulo: editando ? (edicao?.numero_documento ?? 'Editar') : 'Novo lançamento' },
      ]}
      voltarPara="auto"
      titulo={editando ? 'Editar lançamento' : 'Novo lançamento'}
      subtitulo="Um título lançado à mão — para o que não vem de documento do sistema"
    >
      <FormularioDeLancamento
        ladoInicial={lado}
        edicao={edicao}
        aoFechar={voltar}
        aoConcluir={(aviso) => {
          // O aviso vai para a barra e sobrevive à navegação: a tela que
          // salvou desmonta no mesmo instante, e uma faixa dentro dela não
          // seria vista por ninguém.
          mostrarAviso(aviso)
          voltar()
        }}
      />
    </LayoutApp>
  )
}
